import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import { Close as CloseIcon } from "@material-ui/icons";
import Modal from "./modal";
import DocumentPreview from "./DocumentPreview";
import { DOCUMENT_ACTION } from "../redux/action";

function inferMimeType(ext) {
  const e = (ext || "").toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"].includes(e)) {
    return `image/${e === "svg" ? "svg+xml" : e === "jpg" ? "jpeg" : e}`;
  }
  if (e === "pdf") return "application/pdf";
  if (e === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (e === "doc") return "application/msword";
  if (e === "xlsx") {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (e === "xls") return "application/vnd.ms-excel";
  return "";
}

function deriveFileMeta(url) {
  let pathname = "";
  try {
    pathname = new URL(url).pathname;
  } catch (e) {
    pathname = url;
  }
  let key = pathname.replace(/^\/+/, "");
  try {
    key = decodeURIComponent(key);
  } catch (e) {
    /* keep encoded if decode fails */
  }
  const name = key.split("/").pop() || "File";
  const ext = (name.includes(".") ? name.split(".").pop() : "").toLowerCase();
  return { fileURL: key, document_name: name, ext };
}

export default function EfilingSignedConsentsDisplay({ value }) {
  const urls = useMemo(
    () =>
      String(value ?? "")
        .split(",")
        .map((u) => u.trim())
        .filter((u) => u && u.toLowerCase() !== "null"),
    [value],
  );

  const [previewOpen, setPreviewOpen] = useState(false);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [blobUrl, setBlobUrl] = useState("");
  const [docType, setDocType] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const blobUrlRef = useRef("");

  useEffect(() => {
    blobUrlRef.current = blobUrl;
  }, [blobUrl]);

  const revokeBlob = useCallback(() => {
    if (blobUrlRef.current) {
      try {
        URL.revokeObjectURL(blobUrlRef.current);
      } catch (e) {
        /* ignore */
      }
      blobUrlRef.current = "";
    }
  }, []);

  useEffect(() => {
    return () => {
      revokeBlob();
    };
  }, [revokeBlob]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setOriginalUrl(null);
    setBlobUrl("");
    setDocType("");
    setDocumentName("");
    setError("");
    setLoading(false);
    revokeBlob();
  }, [revokeBlob]);

  const loadPreview = useCallback(
    async (url) => {
      revokeBlob();
      const meta = deriveFileMeta(url);
      const document_type = inferMimeType(meta.ext);
      setOriginalUrl(url);
      setDocType(document_type);
      setDocumentName(meta.document_name);
      setPreviewOpen(true);
      setLoading(true);
      setError("");
      setBlobUrl("");
      try {
        const response = await DOCUMENT_ACTION.downloadViewFile({
          fileURL: meta.fileURL,
          document_name: meta.document_name,
          document_type,
          type: "Preview",
        });
        const raw = response && response.raw;
        if (!raw) throw new Error("No preview returned");
        setBlobUrl(raw);
        blobUrlRef.current = raw;
      } catch (e) {
        setError("Could not load preview for this file.");
      } finally {
        setLoading(false);
      }
    },
    [revokeBlob],
  );

  if (!urls.length) return "—";

  return (
    <>
      <span className="efiling-signed-consents-nums">
        {urls.map((url, i) => (
          <React.Fragment key={`${i}-${url.slice(0, 48)}`}>
            {i > 0 ? (
              <span className="efiling-signed-consents-sep">, </span>
            ) : null}
            <button
              type="button"
              className="efiling-signed-consents-btn"
              title={url}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                loadPreview(url);
              }}
            >
              {i + 1}
            </button>
          </React.Fragment>
        ))}
      </span>

      <Modal
        title={null}
        open={previewOpen}
        handleClose={closePreview}
        maxWidth
        className="efiling-create-modal efiling-preview-modal"
      >
        <div className="efiling-modal-shell">
          <div className="efiling-modal-header-row">
            <h3 className="efiling-modal-title">Preview</h3>
            <IconButton
              aria-label="close"
              className="efiling-modal-close-btn"
              onClick={closePreview}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="efiling-preview-body">
            {loading ? (
              <div className="efiling-preview-loading">Loading preview…</div>
            ) : error ? (
              <div className="efiling-preview-fallback">
                <p>{error}</p>
                {originalUrl ? (
                  <a
                    href={originalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="efiling-preview-open-link"
                  >
                    Open in new tab
                  </a>
                ) : null}
              </div>
            ) : blobUrl ? (
              <DocumentPreview
                blobUrl={blobUrl}
                documentType={docType}
                documentName={documentName}
                className="efiling-preview-frame"
              />
            ) : null}
          </div>
        </div>
      </Modal>
    </>
  );
}
